import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { createSearchParams } from "react-router-dom";
import { invalidateOn } from "@/helpers/reduxHelpers";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import {
  transformAddOns,
  transformOffloadingConfiguration,
  transformInvoicingConfiguration,
  transformGoogleTagManagerConfiguration,
} from "./helpers";
import {
  ToggleAddOnParams,
  GetOffloadingConfigurationResponse,
  UpdateOffloadingConfigurationParams,
  GetInvoicingConfigurationResponse,
  UpdateInvoicingConfigurationParams,
  GetAddOnsParams,
  GetGoogleTagManagerConfigurationResponse,
  UpdateGoogleTagManagerConfigurationParams,
  GetAddOnsResponse,
} from "./types";

export const addOnApi = createApi({
  reducerPath: "addOnApi",
  baseQuery: baseQueryWithProvider("add-on"),
  endpoints: (builder) => ({
    getPremiumAddOns: builder.query<GetAddOnsResponse, void>({
      query: () => "?premium=1",
      providesTags: ["AddOns"],
      transformResponse: transformAddOns,
    }),
    getAddOns: builder.query<GetAddOnsResponse, GetAddOnsParams>({
      query: (params) => `?premium=0&${createSearchParams(params).toString()}`,
      providesTags: ["AddOns"],
      transformResponse: transformAddOns,
    }),
    getFeaturedAddOns: builder.query<GetAddOnsResponse, void>({
      query: () => "?featured=true",
      providesTags: ["AddOns"],
      transformResponse: transformAddOns,
    }),
    toggleAddOn: builder.mutation<void, ToggleAddOnParams>({
      query: ({ id, enabled }) => ({
        url: `/${id}`,
        method: "POST",
        body: { enabled },
      }),
      invalidatesTags: invalidateOn({ success: ["AddOns"] }),
    }),
    getOffloadingConfiguration: builder.query<GetOffloadingConfigurationResponse, void>({
      query: () => "/offloading/configuration",
      providesTags: ["OffloadingAddOnConfiguration"],
      transformResponse: transformOffloadingConfiguration,
    }),
    updateOffloadingConfiguration: builder.mutation<void, UpdateOffloadingConfigurationParams>({
      query: (body) => ({
        url: "/offloading/configuration",
        method: "POST",
        body,
      }),
      invalidatesTags: invalidateOn({ success: ["OffloadingAddOnConfiguration"] }),
    }),
    getInvoicingConfiguration: builder.query<GetInvoicingConfigurationResponse, void>({
      query: () => "/invoicing/configuration",
      transformResponse: transformInvoicingConfiguration,
    }),
    updateInvoicingConfiguration: builder.mutation<void, UpdateInvoicingConfigurationParams>({
      query: (body) => ({
        url: "/invoicing/configuration",
        method: "POST",
        body,
      }),
    }),
    getGoogleTagManagerConfiguration: builder.query<GetGoogleTagManagerConfigurationResponse, void>({
      query: () => "/enterprise-gtm/configuration",
      transformResponse: transformGoogleTagManagerConfiguration,
    }),
    updateGoogleTagManagerConfiguration: builder.mutation<void, UpdateGoogleTagManagerConfigurationParams>({
      query: (body) => ({
        url: "/enterprise-gtm/configuration",
        method: "POST",
        body,
      }),
    }),
  }),
  tagTypes: ["AddOns", "OffloadingAddOnConfiguration"],
});

export const {
  useGetPremiumAddOnsQuery,
  useGetAddOnsQuery,
  useGetFeaturedAddOnsQuery,
  useToggleAddOnMutation,
  useGetOffloadingConfigurationQuery,
  useLazyGetOffloadingConfigurationQuery,
  useUpdateOffloadingConfigurationMutation,
  useLazyGetInvoicingConfigurationQuery,
  useUpdateInvoicingConfigurationMutation,
  useLazyGetGoogleTagManagerConfigurationQuery,
  useUpdateGoogleTagManagerConfigurationMutation,
} = addOnApi;
